/**
 * Booking Service V2
 *
 * Clean service that matches V2 API response structure.
 * NO transformation logic - API is source of truth.
 */

import { buildApiUrl } from '@/utils/apiHelpers';
import { logger } from '@/utils/logger';
import type { BookingV2, BookingV2Response, BookingV2SingleResponse } from '@/types/booking';
import { postWithAvailabilityOverride } from '@/utils/availabilityOverride';

const log = logger.module('BookingServiceV2');

// ========================================
// Helper Functions
// ========================================

/**
 * Get auth headers with token
 */
function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

/**
 * Handle API errors consistently
 */
function handleApiError(error: unknown, context: string): never {
  log.error(`${context} failed`, error);
  if (error instanceof Error) {
    throw error;
  }
  throw new Error(`${context} failed`);
}

// ========================================
// Booking Service V2
// ========================================

export const bookingServiceV2 = {

  /**
   * Get all bookings with optional filters
   * GET /api/v2/admin/bookings
   */
  async getAllBookings(options?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
    corporateAccountId?: string;
  }): Promise<{ bookings: BookingV2[]; pagination?: any }> {
    try {
      log.info('Fetching all bookings', { options });

      // Build query parameters
      const params = new URLSearchParams();
      if (options?.page) params.append('page', options.page.toString());
      params.append('limit', (options?.limit || 50).toString());
      if (options?.status) params.append('status', options.status);
      if (options?.search) params.append('search', options.search);
      if (options?.corporateAccountId) params.append('corporateAccountId', options.corporateAccountId);

      const queryString = params.toString();
      const url = buildApiUrl(`admin/bookings${queryString ? `?${queryString}` : ''}`, 'v2');

      log.debug('Making API request', { url });

      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const errorText = await response.text();
        log.error('API request failed', { status: response.status, errorText });
        throw new Error(`Failed to fetch bookings: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && Array.isArray(result.data)) {
        log.info('Bookings fetched successfully', { count: result.data.length });
        return {
          bookings: result.data as BookingV2[],
          pagination: result.pagination
        };
      }

      log.warn('Unexpected response structure', { result });
      return { bookings: [] };

    } catch (error) {
      return handleApiError(error, 'getAllBookings');
    }
  },

  /**
   * Get a single booking by ID
   * GET /api/v2/admin/bookings/:id
   * NOTE: Returns nested structure, not flat like list endpoint
   */
  async getBookingById(bookingId: string): Promise<BookingV2SingleResponse> {
    try {
      log.info('Fetching booking by ID', { bookingId });

      const url = buildApiUrl(`admin/bookings/${bookingId}`, 'v2');
      console.log('🌐 V2 getBookingById - Calling URL:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const errorText = await response.text();
        log.error('API request failed', { status: response.status, errorText });
        throw new Error(`Failed to fetch booking: ${response.status}`);
      }

      const result = await response.json();

      console.log('🔍 V2 getBookingById - Full API Response:', JSON.stringify(result, null, 2));

      if (result.success && result.data) {
        log.info('Booking fetched successfully', { bookingId: result.data.id || result.data._id });
        return result.data;
      }

      // Maybe backend returns booking directly in success.booking?
      if (result.success && result.booking) {
        log.info('Booking fetched from result.booking', { bookingId: result.booking.id });
        return result.booking;
      }

      throw new Error('Booking not found');

    } catch (error) {
      return handleApiError(error, 'getBookingById');
    }
  },

  /**
   * Update a booking (simple status/amount updates)
   * PUT /api/v2/admin/bookings/:id
   */
  async updateBooking(bookingId: string, updateData: {
    status?: string;
    totalAmount?: number;
    startDate?: string;
    endDate?: string;
    type?: string;
  }): Promise<BookingV2> {
    try {
      log.info('Updating booking', { bookingId, updateData });

      const url = buildApiUrl(`admin/bookings/${bookingId}`, 'v2');

      const response = await fetch(url, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        log.error('Update failed', { status: response.status, errorText });
        throw new Error(`Failed to update booking: ${response.status}`);
      }

      const result: BookingV2Response = await response.json();

      if (result.success && result.data) {
        log.info('Booking updated successfully', { bookingId: result.data._id });
        return result.data;
      }

      throw new Error('Update failed');

    } catch (error) {
      return handleApiError(error, 'updateBooking');
    }
  },

  /**
   * Extend booking end date
   * PUT /api/v2/admin/bookings/:id/extend
   */
  async extendBooking(bookingId: string, extendData: {
    newEndDate: string;
    newEndTime?: string;
    additionalCost?: number;
    adminNotes?: string;
  }): Promise<any> {
    try {
      log.info('Extending booking', { bookingId, newEndDate: extendData.newEndDate });

      const url = buildApiUrl(`admin/bookings/${bookingId}/extend`, 'v2');

      const response = await fetch(url, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(extendData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        log.error('Extend failed', { status: response.status, errorText });

        // Try to parse error details
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.error?.code === 'NOT_AVAILABLE' && errorData.error?.details?.conflictingBookings) {
            const conflicts = errorData.error.details.conflictingBookings
              .map((b: any) => `${b.bookingNumber} (${b.startDate} - ${b.endDate})`)
              .join(', ');
            throw new Error(`Not available for extended dates. Conflicts: ${conflicts}`);
          }
          throw new Error(errorData.error?.message || `Failed to extend booking: ${response.status}`);
        } catch (parseError) {
          throw new Error(`Failed to extend booking: ${response.status} - ${errorText}`);
        }
      }

      const result = await response.json();

      if (result.success && result.data) {
        log.info('Booking extended successfully', {
          bookingId: result.data.bookingId,
          newEndDate: result.data.newEndDate,
          additionalCost: result.data.additionalCost
        });
        return result.data;
      }

      throw new Error('Extend failed');

    } catch (error) {
      return handleApiError(error, 'extendBooking');
    }
  },

  /**
   * Modify a booking (dates, locations, notes, etc.)
   * PUT /api/v2/admin/bookings/:id/modify
   */
  async modifyBooking(bookingId: string, modifyData: {
    // Location fields
    pickupLocation?: string;
    dropoffLocation?: string;
    // Date/time fields
    startDate?: string;
    endDate?: string;
    startTime?: string;
    endTime?: string;
    // Special requests
    specialRequests?: string;
    // Notes fields (stored in JSON notes column)
    insurance?: boolean;
    delivery?: boolean;
    deliveryAddress?: string;
    catering?: boolean;
    watersports?: boolean;
    guests?: number;
    // Cost adjustments
    additionalCost?: number;
    newTotalAmount?: number;
    // Admin notes
    adminNotes?: string;
  }): Promise<any> {
    try {
      log.info('Modifying booking', { bookingId, modifyData });

      const url = buildApiUrl(`admin/bookings/${bookingId}/modify`, 'v2');

      const response = await fetch(url, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(modifyData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        log.error('Modify failed', { status: response.status, errorText });

        // Try to parse error details
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.error?.code === 'NOT_AVAILABLE' && errorData.error?.details?.conflictingBookings) {
            const conflicts = errorData.error.details.conflictingBookings
              .map((b: any) => `${b.bookingNumber} (${b.startDate} - ${b.endDate})`)
              .join(', ');
            throw new Error(`Not available for new dates. Conflicts: ${conflicts}`);
          }
          throw new Error(errorData.error?.message || `Failed to modify booking: ${response.status}`);
        } catch (parseError) {
          throw new Error(`Failed to modify booking: ${response.status} - ${errorText}`);
        }
      }

      const result = await response.json();

      if (result.success && result.data) {
        log.info('Booking modified successfully', {
          bookingId: result.data.bookingId,
          costChanged: result.data.costChanged,
          newAmount: result.data.newTotalAmount
        });
        return result.data;
      }

      throw new Error('Modify failed');

    } catch (error) {
      return handleApiError(error, 'modifyBooking');
    }
  },

  /**
   * Get car rental bookings (filter by vehicle type)
   */
  async getCarRentals(options?: { page?: number; limit?: number; status?: string }): Promise<{ bookings: BookingV2[]; pagination?: any }> {
    try {
      const result = await this.getAllBookings(options);

      // Filter for vehicle rentals
      const carRentals = result.bookings.filter((booking) => {
        return booking.bookingEntityType === 'vehicle';
      });

      log.info('Car rentals filtered', { total: carRentals.length });
      return {
        bookings: carRentals,
        pagination: result.pagination
      };

    } catch (error) {
      return handleApiError(error, 'getCarRentals');
    }
  },

  /**
   * Get driver rental bookings
   */
  async getDriverRentals(options?: { page?: number; limit?: number; status?: string }): Promise<{ bookings: BookingV2[]; pagination?: any }> {
    try {
      const result = await this.getAllBookings(options);

      // Filter for driver-only bookings
      const driverRentals = result.bookings.filter((booking) => {
        return booking.bookingEntityType === 'driver';
      });

      log.info('Driver rentals filtered', { total: driverRentals.length });
      return {
        bookings: driverRentals,
        pagination: result.pagination
      };

    } catch (error) {
      return handleApiError(error, 'getDriverRentals');
    }
  },

  /**
   * Get yacht rental bookings
   */
  async getYachtRentals(options?: { page?: number; limit?: number; status?: string }): Promise<{ bookings: BookingV2[]; pagination?: any }> {
    try {
      const result = await this.getAllBookings(options);

      // Filter for yacht bookings
      const yachtRentals = result.bookings.filter((booking) => {
        return booking.bookingEntityType === 'yacht';
      });

      log.info('Yacht rentals filtered', { total: yachtRentals.length });
      return {
        bookings: yachtRentals,
        pagination: result.pagination
      };

    } catch (error) {
      return handleApiError(error, 'getYachtRentals');
    }
  },

  /**
   * Get bookings by category
   */
  async getBookingsByCategory(category: string, options?: { page?: number; limit?: number }): Promise<BookingV2[]> {
    try {
      const allBookings = await this.getAllBookings(options);

      if (category === 'all') {
        return allBookings;
      }

      // Filter by entity type
      const filtered = allBookings.filter((booking) => {
        if (category === 'vehicle-rental' || category === 'rental') {
          return booking.bookingEntityType === 'vehicle';
        }
        if (category === 'driver-rental' || category === 'driver-hire') {
          return booking.bookingEntityType === 'driver';
        }
        if (category === 'yacht-rental' || category === 'yacht') {
          return booking.bookingEntityType === 'yacht';
        }
        return false;
      });

      log.info('Bookings filtered by category', { category, total: filtered.length });
      return filtered;

    } catch (error) {
      return handleApiError(error, 'getBookingsByCategory');
    }
  },

  /**
   * Get corporate account bookings
   */
  async getCorporateAccountBookings(
    corporateAccountId: string,
    options?: { page?: number; limit?: number; status?: string }
  ): Promise<BookingV2[]> {
    try {
      log.info('Fetching corporate account bookings', { corporateAccountId, options });

      return await this.getAllBookings({
        ...options,
        corporateAccountId
      });

    } catch (error) {
      return handleApiError(error, 'getCorporateAccountBookings');
    }
  },

  /**
   * Cancel a booking
   * POST /api/v2/admin/bookings/:id/cancel
   *
   * V2 Response includes:
   * - bookingId, bookingReference, status, cancellationReason
   * - suggestedRefund: { refundableAmount, penaltyAmount, securityDepositRefund, totalRefund, penaltyPercentage, message }
   * - nextSteps: { message, refundEndpoint }
   */
  async cancelBooking(bookingId: string, cancellationData: {
    reason: string;
    adminNotes?: string;
  }): Promise<any> {
    try {
      log.info('Cancelling booking', { bookingId, reason: cancellationData.reason });

      const url = buildApiUrl(`admin/bookings/${bookingId}/cancel`, 'v2');

      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(cancellationData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        log.error('Cancel failed', { status: response.status, errorText });
        throw new Error(`Failed to cancel booking: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        log.info('Booking cancelled successfully', {
          bookingId: result.data.bookingId,
          suggestedRefund: result.data.suggestedRefund?.totalRefund
        });
        return result.data;
      }

      throw new Error('Cancel failed');

    } catch (error) {
      return handleApiError(error, 'cancelBooking');
    }
  },

  /**
   * Assign driver to booking
   * POST /api/v2/admin/bookings/:id/assign-driver
   */
  async assignDriver(bookingId: string, driverId: string, notes?: string): Promise<BookingV2> {
    try {
      log.info('Assigning driver to booking', { bookingId, driverId });

      const url = buildApiUrl(`admin/bookings/${bookingId}/assign-driver`, 'v2');

      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ driverId, notes })
      });

      if (!response.ok) {
        const errorText = await response.text();
        log.error('Assign driver failed', { status: response.status, errorText });
        throw new Error(`Failed to assign driver: ${response.status}`);
      }

      const result: BookingV2Response = await response.json();

      if (result.success && result.data) {
        log.info('Driver assigned successfully', { bookingId: result.data._id, driverId });
        return result.data;
      }

      throw new Error('Assign driver failed');

    } catch (error) {
      return handleApiError(error, 'assignDriver');
    }
  },

  /**
   * Create admin booking
   * POST /api/v2/admin/bookings
   */
  async createAdminBooking(bookingData: {
    userId: string;
    vehicleId?: string;
    startDate: string;
    endDate: string;
    pickupLocation: string;
    dropoffLocation?: string;
    totalAmount: number;
    serviceCategory?: string;
    serviceType?: string;
    bookingType?: string;
    status?: string;
    paymentStatus?: string;
    customerName?: string;
    customerEmail?: string;
    customerPhone?: string;
    notes?: string;
    adminId?: string;
    corporateAccountId?: string;
  }): Promise<BookingV2> {
    try {
      log.info('Creating admin booking', { userId: bookingData.userId });

      const url = buildApiUrl('admin/bookings', 'v2');

      const response = await postWithAvailabilityOverride((body) => fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body)
      }), bookingData as Record<string, unknown>);

      if (!response.ok) {
        const errorText = await response.text();
        log.error('Create booking failed', { status: response.status, errorText });
        throw new Error(`Failed to create booking: ${response.status}`);
      }

      const result: BookingV2Response = await response.json();

      if (result.success && result.data) {
        log.info('Booking created successfully', { bookingId: result.data._id });
        return result.data;
      }

      throw new Error('Create booking failed');

    } catch (error) {
      return handleApiError(error, 'createAdminBooking');
    }
  },

  /**
   * Get cancellation preview
   * GET /api/v2/admin/bookings/:id/cancellation-preview
   */
  async getCancellationPreview(bookingId: string): Promise<any> {
    try {
      log.info('Fetching cancellation preview', { bookingId });

      const url = buildApiUrl(`admin/bookings/${bookingId}/cancellation-preview`, 'v2');

      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const errorText = await response.text();
        log.error('Preview request failed', { status: response.status, errorText });
        throw new Error(`Failed to fetch cancellation preview: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        log.info('Preview fetched successfully');
        return result.data;
      }

      throw new Error('Failed to get cancellation preview');

    } catch (error) {
      return handleApiError(error, 'getCancellationPreview');
    }
  },

  /**
   * Issue manual refund
   * POST /api/v2/admin/bookings/:id/refund
   */
  async issueRefund(bookingId: string, refundData: {
    refundAmount: number;
    refundReason: string;
    adminNotes?: string;
    refundMethod?: string;
    transitionToRefundedState?: boolean;
  }): Promise<any> {
    try {
      log.info('Issuing refund', { bookingId, amount: refundData.refundAmount });

      const url = buildApiUrl(`admin/bookings/${bookingId}/refund`, 'v2');

      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(refundData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        log.error('Refund failed', { status: response.status, errorText });
        throw new Error(`Failed to issue refund: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        log.info('Refund issued successfully', { refundNumber: result.data.refund?.refundNumber });
        return result.data;
      }

      throw new Error('Refund failed');

    } catch (error) {
      return handleApiError(error, 'issueRefund');
    }
  },

  /**
   * Release security deposit
   * POST /api/v2/admin/bookings/:id/release-deposit
   */
  async releaseDeposit(bookingId: string, releaseData: {
    releaseType: 'full' | 'partial' | 'forfeit';
    chargedAmount?: number;
    damageDescription?: string;
    damagePhotos?: string[];
    forfeitReason?: string;
    adminNotes?: string;
  }): Promise<any> {
    try {
      log.info('Releasing security deposit', { bookingId, releaseType: releaseData.releaseType });

      const url = buildApiUrl(`admin/bookings/${bookingId}/release-deposit`, 'v2');

      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(releaseData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        log.error('Release deposit failed', { status: response.status, errorText });

        // Try to parse error message from backend
        let errorMessage = `Failed to release deposit: ${response.status}`;
        try {
          const errorJson = JSON.parse(errorText);
          // Check for nested error object with message
          if (errorJson.error && typeof errorJson.error === 'object' && errorJson.error.message) {
            errorMessage = errorJson.error.message;
          } else if (errorJson.message) {
            errorMessage = errorJson.message;
          } else if (errorJson.error && typeof errorJson.error === 'string') {
            errorMessage = errorJson.error;
          }
        } catch (e) {
          // errorText is not JSON, use as is
          if (errorText) {
            errorMessage = errorText;
          }
        }

        throw new Error(errorMessage);
      }

      const result = await response.json();

      if (result.success && result.data) {
        log.info('Deposit released successfully', {
          releaseType: result.data.releaseType,
          refundAmount: result.data.refundAmount
        });
        return result.data;
      }

      throw new Error('Release deposit failed');

    } catch (error) {
      return handleApiError(error, 'releaseDeposit');
    }
  },

  /**
   * Get booking history
   * GET /api/v2/admin/bookings/:id/history
   */
  async getBookingHistory(bookingId: string): Promise<any> {
    try {
      log.info('Fetching booking history', { bookingId });

      const url = buildApiUrl(`admin/bookings/${bookingId}/history`, 'v2');

      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const errorText = await response.text();
        log.error('Get history failed', { status: response.status, errorText });
        throw new Error(`Failed to get booking history: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        log.info('Booking history retrieved', { bookingId, historyCount: result.data.history?.length || 0 });
        return result.data;
      }

      throw new Error('Get history failed');

    } catch (error) {
      return handleApiError(error, 'getBookingHistory');
    }
  },

  /**
   * Get check-in/checkout details for booking
   * GET /api/v2/admin/bookings/:id/checkin-checkout
   */
  async getCheckinCheckoutDetails(bookingId: string): Promise<any> {
    try {
      log.info('Fetching check-in/checkout details', { bookingId });

      const url = buildApiUrl(`admin/bookings/${bookingId}/checkin-checkout`, 'v2');

      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const errorText = await response.text();
        log.error('Get check-in/checkout details failed', { status: response.status, errorText });
        throw new Error(`Failed to get check-in/checkout details: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        log.info('Check-in/checkout details retrieved', { bookingId });

        // Transform V2 response to match CheckinCheckoutSection component expectations
        const transformedData = {
          ...result.data,
          booking: {
            id: result.data.bookingId || result.data.booking?.id,
            bookingNumber: result.data.bookingNumber || result.data.booking?.bookingNumber,
            renterName: result.data.renter?.name || 'N/A',
            hostName: result.data.host?.name || 'N/A',
            vehicleName: result.data.vehicle ? `${result.data.vehicle.make} ${result.data.vehicle.model}`.trim() : 'N/A'
          }
        };

        return {
          success: true,
          data: transformedData
        };
      }

      throw new Error('Get check-in/checkout details failed');

    } catch (error) {
      return handleApiError(error, 'getCheckinCheckoutDetails');
    }
  },

  /**
   * Get full service type display (UI helper - for display purposes only)
   */
  getServiceTypeDisplayFull(serviceCategory: string | undefined, serviceType: string): { label: string; icon: string } {
    // Normalize field values (handle both snake_case and kebab-case)
    const normalizedCategory = serviceCategory?.toLowerCase().replace(/_/g, '-');
    const normalizedType = serviceType?.toLowerCase().replace(/_/g, '-');

    // Handle vehicle-rental with chauffeur = "Car with Driver"
    if (normalizedCategory === 'vehicle-rental') {
      if (normalizedType === 'chauffeur-driven') {
        return { label: 'Car with Driver', icon: '🚗👨‍💼' };
      }
      if (normalizedType === 'airport-transfer') {
        return { label: 'Airport Transfer', icon: '✈️🚗' };
      }
      if (normalizedType === 'self-drive') {
        return { label: 'Self Drive', icon: '🔑' };
      }
    }

    // Handle driver-rental = "Driver Rental Only"
    if (normalizedCategory === 'driver-rental') {
      if (normalizedType === 'hourly') {
        return { label: 'Driver Hire (Hourly)', icon: '👨‍💼⏱️' };
      }
      if (normalizedType === 'daily') {
        return { label: 'Driver Hire (Daily)', icon: '👨‍💼📅' };
      }
    }

    // Handle yacht-rental
    if (normalizedCategory === 'yacht-rental') {
      if (normalizedType === 'cruise') {
        return { label: 'Yacht Cruise', icon: '🛥️🌊' };
      }
      if (normalizedType === 'trip') {
        return { label: 'Yacht Trip', icon: '🛥️' };
      }
    }

    // Default fallback
    return {
      label: serviceType?.replace(/-/g, ' ').replace(/_/g, ' ') || 'Unknown',
      icon: '❓'
    };
  }
};

// Export as default for convenience
export default bookingServiceV2;
